// PRD.312 — longitudinal Medical History context for the active encounter.
//
// Assembles the read-only right column from sources that ALREADY exist:
//   - patient-scoped history: `patients.medical_history` via `qPatientById`
//     (the same row the patient record/create/edit workflow edits — no
//     duplicate patient field is introduced);
//   - prior encounter history: PRD.310's `patient_encounters.medical_history`
//     via `qEncountersByPatient` — the canonical per-patient encounter query,
//     already used elsewhere on this page path, so PowerSync shares the watch
//     rather than issuing a second scan;
//   - visit dates: `useVisitDateByEncounter`, the app's canonical resolver
//     (visit.visit_date → visit.started_at → encounter.started_at →
//     encounter.created_at).
//
// Read-only by construction: this hook only queries and selects. It never
// writes, and never copies history into the active encounter.

import { useMemo } from 'react';
import {
  qEncountersByPatient,
  qNoOp,
  qPatientById,
  selectPatientMedicalHistory,
  selectPriorMedicalHistory,
  type PatientDetailRow,
  type PatientEncounterRow,
  type PriorMedicalHistoryEntry,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { useVisitDateByEncounter } from './patient-image-visits';

export interface MedicalHistoryContext {
  /** Patient-record narrative, or null when the patient has none. */
  patientHistory: string | null;
  /** Earlier encounters' narratives, newest first, active encounter excluded. */
  priorEntries: PriorMedicalHistoryEntry[];
}

const EMPTY: MedicalHistoryContext = { patientHistory: null, priorEntries: [] };

/**
 * `enabled` is the clinic's `showPreviousHistory` setting. When off, the
 * queries are not issued at all (`qNoOp`), so turning the panel off also costs
 * nothing to load — the setting gates the work, not just the pixels.
 */
export function useMedicalHistoryContext(
  patientId: string,
  activeEncounterId: string,
  enabled: boolean,
): MedicalHistoryContext {
  const ready = enabled && Boolean(patientId);

  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    ready ? qPatientById(patientId) : qNoOp('medical-history-patient'),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    ready ? qEncountersByPatient(patientId) : qNoOp('medical-history-encounters'),
  );
  const visitDateByEncounter = useVisitDateByEncounter(ready ? patientId : '');

  const patientHistory = useMemo(() => selectPatientMedicalHistory(patientRows[0]), [patientRows]);

  const priorEntries = useMemo(
    () => selectPriorMedicalHistory(encounterRows, activeEncounterId, visitDateByEncounter),
    [encounterRows, activeEncounterId, visitDateByEncounter],
  );

  return ready ? { patientHistory, priorEntries } : EMPTY;
}
